import React, { useEffect, useRef, useState } from "react";
import { ReactComponent as EditIcon } from "../../../myfin_v2/assets/icons/editPri.svg";

/**
 * Props:
 *  value: { tableTitle, tableRow:[{name,lineType}], tableColumn:[{name,lineType}] }
 *  onChange(nextValue)
 *  forceOpen?: boolean
 *  onAfterOpen?: () => void
 *  disabled?: boolean
 */
export default function TemplateTableGenerator({
  value,
  onChange,
  forceOpen = false,
  onAfterOpen,
  disabled = false,
}) {
  const totalRows = 20;
  const totalCols = 20;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hoverRows, setHoverRows] = useState(0);
  const [hoverCols, setHoverCols] = useState(0);
  const [selectedRows, setSelectedRows] = useState(0);
  const [selectedCols, setSelectedCols] = useState(0);
  const [tableName, setTableName] = useState(value?.tableTitle || "");
  const [generated, setGenerated] = useState(false);
  const [cellValues, setCellValues] = useState({});

  const modalPrefilledRef = useRef(false);
  const skipPrefillOnCloseRef = useRef(false);
  const lastSentPayloadRef = useRef(null);


  const openModal = () => {
    if (disabled) return;
    setHoverRows(0);
    setHoverCols(0);
    setIsModalOpen(true);
  };

  const closeModal = () => setIsModalOpen(false);

  useEffect(() => {
    if (forceOpen && !disabled) {
      openModal();
      onAfterOpen?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceOpen, disabled]);

  const initCells = (rows, cols) => {
    const next = {};
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) next[`${r}-${c}`] = "";
    }
    return next;
  };

  const applyValueToState = (incoming) => {
    if (!incoming) return;

    const rows = (incoming.tableRow?.length || 0) + 1;
    const cols = incoming.tableColumn?.length || 0;

    if (rows <= 1 || cols <= 0) {
      setGenerated(false);
      setTableName(incoming.tableTitle || "");
      setSelectedRows(0);
      setSelectedCols(0);
      setCellValues({});
      return;
    }

    const base = initCells(rows, cols);

    incoming.tableColumn.forEach((c, i) => {
      base[`0-${i}`] = c?.name ?? "";
    });

    incoming.tableRow.forEach((r, i) => {
      base[`${i + 1}-0`] = r?.name ?? "";
    });

    setSelectedRows(rows);
    setSelectedCols(cols);
    setCellValues(base);
    setTableName(incoming.tableTitle || "");
    setGenerated(true);
  };

  useEffect(() => {
    if (!isModalOpen && skipPrefillOnCloseRef.current) {
      skipPrefillOnCloseRef.current = false;
      return;
    }
    if (!isModalOpen) applyValueToState(value);
  }, [value, isModalOpen]);

  useEffect(() => {
    if (isModalOpen) {
      if (!modalPrefilledRef.current) {
        applyValueToState(value);
        modalPrefilledRef.current = true;
      }
    } else {
      modalPrefilledRef.current = false;
    }
  }, [isModalOpen, value]);

  const handleModalUpdate = () => {
    if (disabled) return;

    const rows = selectedRows || hoverRows;
    const cols = selectedCols || hoverCols;
    if (!rows || !cols) return;

    const prev = cellValues || {};
    const merged = initCells(rows, cols);

    Object.keys(prev).forEach((k) => {
      const [r, c] = k.split("-").map(Number);
      if (r < rows && c < cols) merged[k] = prev[k];
    });

    setSelectedRows(rows);
    setSelectedCols(cols);
    setCellValues(merged);
    setGenerated(true);
    skipPrefillOnCloseRef.current = true;
    closeModal();
  };

  const handleCellChange = (r, c, v) => {
    if (disabled) return;
    setCellValues((prev) => ({ ...prev, [`${r}-${c}`]: v }));
  };

  const computePayload = () => {
    const tableColumn = [];
    for (let c = 0; c < selectedCols; c++) {
      tableColumn.push({
        name: (cellValues[`0-${c}`] ?? "").trim(),
        lineType: "line item",
      });
    }

    const tableRow = [];
    for (let r = 1; r < selectedRows; r++) {
      tableRow.push({
        name: (cellValues[`${r}-0`] ?? "").trim(),
        lineType: "line item",
      });
    }

    return {
      tableTitle: tableName ?? "",
      tableRow,
      tableColumn,
    };
  };

  useEffect(() => {
    if (disabled || !generated || isModalOpen) return;

    const payload = computePayload();
    const payloadStr = JSON.stringify(payload);

    if (lastSentPayloadRef.current === payloadStr) return;
    lastSentPayloadRef.current = payloadStr;

    onChange?.(payload);
  }, [
    disabled,
    cellValues,
    tableName,
    selectedRows,
    selectedCols,
    generated,
    isModalOpen,
  ]);

  return (
    <div
      style={{
        margin: "8px 0",
        border: "1px solid #e3efe7",
        borderRadius: 12,
        padding: 12,
        marginTop: "-33px",
        backgroundColor: "#fff",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 10,
          alignItems: "center",
        }}
      >
        <h5 style={{ margin: 0, color: "#205a46" }}>
          {tableName || "Untitled Table"}
        </h5>

        {/* ICON COMPLETELY HIDDEN WHEN DISABLED */}
        {!disabled && <EditIcon onClick={openModal} />}
      </div>

      {generated && (
        <div style={{ marginTop: 12 }}>
          <table
            style={{
              borderCollapse: "collapse",
              width: "100%",
              border: "1px solid #ccc",
            }}
          >
            <tbody>
              {[...Array(selectedRows)].map((_, r) => (
                <tr key={r}>
                  {[...Array(selectedCols)].map((_, c) => {
                    const editable = r === 0 || c === 0;
                    const key = `${r}-${c}`;
                    return (
                      <td
                        key={c}
                        style={{
                          border: "1px solid #ccc",
                          padding: "6px 8px",
                          minWidth: "120px",
                          background: editable ? "#fff" : "#f8f8f8",
                        }}
                      >
                        {editable ? (
                          <input
                            type="text"
                            value={cellValues[key] ?? ""}
                            disabled={disabled}
                            onChange={(e) =>
                              handleCellChange(r, c, e.target.value)
                            }
                            style={{
                              width: "100%",
                              border: "none",
                              outline: "none",
                              background: "transparent",
                              fontSize: 14,
                              color: "#222",
                              padding:0
                            }}
                          />
                        ) : (
                          <span style={{ color: "#555" }}>
                            {cellValues[key] || ""}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isModalOpen && !disabled && (
        <div
          aria-modal="true"
          role="dialog"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 99,
            padding: 16,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div
            style={{
              width: "min(720px, 96vw)",
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
              padding: 20,
            }}
          >
            <h3>Configure Table</h3>

            <input
              name="tableName"
              value={tableName}
              onChange={(e) => setTableName(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid #d0d0d0",
                marginBottom: 16,
                backgroundColor: "#f8fff8",
              }}
            />

            <SizePicker
              totalRows={totalRows}
              totalCols={totalCols}
              hoverRows={hoverRows}
              hoverCols={hoverCols}
              selectedRows={selectedRows}
              selectedCols={selectedCols}
              onHover={(r, c) => {
                setHoverRows(r);
                setHoverCols(c);
              }}
              onPick={(r, c) => {
                setSelectedRows(r);
                setSelectedCols(c);
                setHoverRows(r);
                setHoverCols(c);
              }}
            />
             <div style={{ marginTop: 8, fontWeight: 500, color: "#333" }}>
              {(selectedRows || hoverRows) > 0 &&
              (selectedCols || hoverCols) > 0
                ? `${selectedRows || hoverRows} Rows × ${
                    selectedCols || hoverCols
                  } Columns`
                : "Select table size"}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button 
                style={{
                  backgroundColor: "#205a46",
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  padding: "10px 16px",
                  cursor: "pointer",
                }} onClick={closeModal}>Cancel</button>
              <button 
                style={{
                  backgroundColor: "#205a46",
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  padding: "10px 16px",
                  cursor: "pointer",
                }} 
                 disabled={
                  // require an explicit selected size OR a preview (hover)
                  !(selectedRows || hoverRows) || !(selectedCols || hoverCols)
                }
                title={
                  !(selectedRows || hoverRows) || !(selectedCols || hoverCols)
                    ? "Pick a size first"
                    : "Create table"
                }
                onClick={handleModalUpdate}>Update</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SizePicker({
  totalRows,
  totalCols,
  hoverRows,
  hoverCols,
  selectedRows,
  selectedCols,
  onHover,
  onPick,
}) {
  return (
    <div
      style={{
        display: "inline-block",
        padding: 10,
        backgroundColor: "#fafafa",
        borderRadius: 10,
        border: "1px solid #e3e3e3",
      }}
    >
      {[...Array(totalRows)].map((_, r) => (
        <div key={r} style={{ display: "flex" }}>
          {[...Array(totalCols)].map((_, c) => {
            const rr = r + 1;
            const cc = c + 1;
            const active =
              rr <= (selectedRows || hoverRows) &&
              cc <= (selectedCols || hoverCols);
            return (
              <div
                key={c}
                onMouseEnter={() => onHover(rr, cc)}
                onClick={() => onPick(rr, cc)}
                style={{
                  width: 16,
                  height: 16,
                  margin: 2,
                  border: "1px solid #ccc",
                  backgroundColor: active ? "#b6e8c4" : "#fff",
                  cursor: "pointer",
                }}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}
